/**
 * RetailerOffer + FlyerDeal — split per T12.0.1.6 (2026-05-05).
 *
 * `retaileroffers` is the persistent catalog: geo-scoped to a StoreLocation,
 * one row per product-at-store. Holds `regular_price` only (no sale price).
 *
 * `flyerdeals` is the time-bound promotional overlay (sourced from Flipp etc.):
 * geo-scoped to a StoreLocation, bounded by valid_from/valid_to. Holds
 * `sale_price` and optionally references a matched `retaileroffers.id`.
 *
 * HouseholdProduct_RetailerOffer_Link and ShoppingListItem.selected_retaileroffer_id reference
 * the global `retaileroffers.id` (no household scoping). T12.0.2.1 reverts the
 * short-lived T12.0.1.6 `selected_flyerdeal_id` field on the SLI: a FlyerDeal
 * already anchors to its RetailerOffer via `matched_retaileroffer_id`, so the SLI
 * has a single offer-side anchor (selected_retaileroffer_id) and deals surface
 * at read time.
 */

import { z } from 'zod';
import { pgNumeric, priceCurrencySchema } from './primitives';

export const retailerOfferSchema = z.object({
  id: z.string(),
  // Geo-scoped only — no `household_id`. Per-household relevance comes from
  // each household's `store_preferences.selected[]`.
  storelocation_id: z.string(),

  // Identity / External References
  external_id: z.string().nullable().optional(),
  retailer_sku: z.string().nullable().optional(),

  // Product Details
  title: z.string(),
  // T12.1.2.2 — `brand` (name) + `brand_logo_url` resolved from the managed
  // `brands` catalog via `retaileroffers.brand_id`, embedded on the read path.
  brand: z.string().nullable().optional(),
  brand_logo_url: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  image_url: z.string().nullable().optional(),

  // T12.5.1 (B) — the resolved SystemProduct id, surfaced from the read JOIN
  // (`retaileroffers.systemproduct_id`). Lets the Deals/Planning surfaces match
  // an offer to a liked/preferred SystemProduct (and to a list item's product)
  // by IDENTITY rather than by a loose product-name title match. Absent on an
  // unresolved offer (LEFT JOIN); the consumer falls back to name matching only
  // for the title axis where no id exists.
  systemproduct_id: z.string().nullable().optional(),
  // T12.1.8.1 — the resolved SystemProduct's display name/image, embedded on
  // the read path from `systemproducts` via `retaileroffers.systemproduct_id`.
  // Optional: present once the sync resolver has clustered this offer into a
  // SystemProduct line; absent on unresolved rows.
  system_product_name: z.string().nullable().optional(),
  system_product_image_url: z.string().nullable().optional(),

  // Packaging
  package_size_value: pgNumeric.nullable().optional(),
  package_size_unit: z.string().nullable().optional(),
  pack_count: pgNumeric,

  // Pricing — catalog: regular_price only.
  regular_price: pgNumeric.nullable().optional(),
  price_currency: priceCurrencySchema,
  price_updated_at: z.string().nullable().optional(),

  created_at: z.string(),
  updated_at: z.string(),
});
export type RetailerOffer = z.infer<typeof retailerOfferSchema>;

export const retailerOfferWithDetailsSchema = retailerOfferSchema.extend({
  store_name: z.string(),
  savings_percent: pgNumeric.nullable().optional(),
  // Derived: true when a flyerdeals row matches this catalog row and is
  // currently active. Catalog rows have no in-table deal fields post-split.
  has_active_deal: z.boolean(),
});
export type RetailerOfferWithDetails = z.infer<typeof retailerOfferWithDetailsSchema>;

export const retailerOffersResponseSchema = z.object({
  offers: z.array(retailerOfferWithDetailsSchema),
  total: z.number(),
  // T26.1 — the offers-by-store endpoint sets this when it found zero offers for
  // a selected, linked store and enqueued an offer-sync job. The client shows a
  // "finding offers…" state and refetches shortly. Optional so the search
  // endpoint (which never sets it) and older clients stay compatible.
  syncTriggered: z.boolean().optional(),
});
export type RetailerOffersResponse = z.infer<typeof retailerOffersResponseSchema>;

// ---------------------------------------------------------------------------
// FlyerDeal — time-bound promotional overlay (T12.0.1.6)
// ---------------------------------------------------------------------------

export const flyerDealSchema = z.object({
  id: z.string(),
  storelocation_id: z.string(),
  // T12.0.12 — canonical multi-source wrapper fields. `source` identifies
  // the adapter (`flipp` today; closed-enum extended per new-adapter Task).
  // `source_url` is the user-facing proof link. `source_item_id` stores the
  // adapter's own per-item identifier verbatim.
  source: z.string(),
  source_url: z.string(),
  source_item_id: z.string(),
  matched_retaileroffer_id: z.string().nullable().optional(),

  title: z.string(),
  brand: z.string().nullable().optional(),
  image_url: z.string().nullable().optional(),
  description: z.string().nullable().optional(),

  regular_price: pgNumeric.nullable().optional(),
  sale_price: pgNumeric,
  price_currency: priceCurrencySchema,
  sale_description: z.string().nullable().optional(),
  valid_from: z.string(),
  valid_to: z.string(),

  created_at: z.string(),
  updated_at: z.string(),
});
export type FlyerDeal = z.infer<typeof flyerDealSchema>;
